import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Card, Space, Typography, Button, Tooltip, Divider, Empty, Grid, message } from 'antd';
import { PictureOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { distActions } from '../../store/dist.slice'; // adjust path if needed
import { useTranslation } from 'react-i18next';

/**
 * MediaCardSelfContained
 * - Fully independent, all logic encapsulated
 * - Mobile-first responsive layout; desktop stays tidy
 * - Logic parity:
 *    • If a video is attached → image upload is disabled
 *    • If one or more images are attached → video upload is disabled
 *    • Drag & drop supported (respects the same mutual-exclusion rules)
 *    • Remove individual image, remove all images, remove video
 * @param {boolean} available: determines if the media container should be available (before/after generation)
 */
const MediaCardSelfContained = ({ available }) => {
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md; // < md → mobile layout

	const [tempImages, setTempImages] = useState([]); // File[]
	const [tempVideo, setTempVideo] = useState(null); // File | null
	const [dragActive, setDragActive] = useState(false);

	const hiddenImageInput = useRef(null);
	const hiddenVideoInput = useRef(null);
	const videoPlayerRef = useRef(null);

	const dispatch = useDispatch();
	const selectedEmployees = useSelector((s) => s.dist.selected_employees || []);

	const { t } = useTranslation();

	const pushImagesToAll = useCallback(
		(files) => {
			// TODO
			if (!files?.length) return;
			// Fan-out to ALL selected employees (keeps current workflow of copying)
			selectedEmployees.forEach((emp) => {
				const id = emp?.id ?? emp;
				// images/video exclusivity: clear video before adding images
				dispatch(distActions.clearTempVideo({ employeeId: id }));
				files.forEach((file) => {
					dispatch(distActions.addTempImage({ employeeId: id, image: file }));
				});
				// Images don’t need opt-out tracking; only video does per requirements
			});
		},
		[selectedEmployees, dispatch],
	);

	const pushVideoToAll = useCallback(
		(file) => {
			// TODO
			if (!file) return;
			selectedEmployees.forEach((emp) => {
				const id = emp?.id ?? emp;
				// exclusivity: clear images, then set video
				dispatch(distActions.clearTempImages({ employeeId: id }));
				dispatch(distActions.setTempVideo({ employeeId: id, video: file }));
			});
		},
		[selectedEmployees, dispatch],
	);

	// Generate object URLs for previews & clean them up to avoid leaks
	const imagePreviews = useMemo(
		() => tempImages.map((file) => ({ file, url: URL.createObjectURL(file) })),
		[tempImages],
	);

	const videoPreview = useMemo(
		() => (tempVideo ? URL.createObjectURL(tempVideo) : null),
		[tempVideo],
	);

	useEffect(() => {
		return () => {
			imagePreviews.forEach(({ url }) => URL.revokeObjectURL(url));
			if (videoPreview) URL.revokeObjectURL(videoPreview);
		};
	}, [imagePreviews, videoPreview]);

	// ===== Handlers =====
	const handleClickImages = useCallback(() => {
		if (!tempVideo) hiddenImageInput.current?.click();
	}, [tempVideo]);

	const handleClickVideo = useCallback(() => {
		if (tempImages.length === 0) hiddenVideoInput.current?.click();
	}, [tempImages.length]);

	const handleResetImages = useCallback(() => {
		// revoke current URLs
		imagePreviews.forEach(({ url }) => URL.revokeObjectURL(url));
		setTempImages([]);
		// also clear images for ALL selected employees
		selectedEmployees.forEach((emp) => {
			const id = emp?.id ?? emp;
			dispatch(distActions.clearTempImages({ employeeId: id }));
		});
	}, [imagePreviews, selectedEmployees, dispatch]);

	const handleRemoveVideo = useCallback(() => {
		if (videoPreview) URL.revokeObjectURL(videoPreview);
		setTempVideo(null);
		// also clear video for ALL selected employees
		selectedEmployees.forEach((emp) => {
			const id = emp?.id ?? emp;
			dispatch(distActions.clearTempVideo({ employeeId: id }));
		});
	}, [videoPreview, selectedEmployees, dispatch]);

	const handleDeleteImage = useCallback((index) => {
		setTempImages((prev) => {
			const next = [...prev];
			// revoke the one being removed
			const toRemove = next[index];
			try {
				const url = URL.createObjectURL(toRemove);
				URL.revokeObjectURL(url);
			} catch (_) {}
			next.splice(index, 1);
			return next;
		});
	}, []);

	const handleImageInputChange = useCallback(
		(e) => {
			const files = Array.from(e.target.files || []);
			if (files.length === 0) return;
			// Respect rule: block images if a video is present
			if (tempVideo) {
				e.target.value = '';
				return;
			}
			const onlyImages = files.filter((f) => f.type.startsWith('image/'));
			if (onlyImages.length) {
				setTempImages((prev) => [...prev, ...onlyImages]);
				// PUSH to all employees (centralized fan-out)
				pushImagesToAll(onlyImages);
			}
			e.target.value = '';
		},
		[tempVideo, pushImagesToAll],
	);

	const handleVideoInputChange = useCallback(
		(e) => {
			const files = Array.from(e.target.files || []);
			if (files.length === 0) return;
			// Respect rule: block video if one or more images exist
			if (tempImages.length > 0) {
				e.target.value = '';
				return;
			}
			const firstVideo = files.find((f) => f.type.startsWith('video/'));
			if (firstVideo) {
				setTempVideo(firstVideo);
				// PUSH to all employees (centralized fan-out)
				pushVideoToAll(firstVideo);
			}
			e.target.value = '';
		},
		[tempImages.length, pushVideoToAll],
	);

	// Drag & Drop
	const handleDragEnter = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(true);
	}, []);
	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);
	const handleDragLeave = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
	}, []);
	const handleDrop = useCallback(
		(e) => {
			e.preventDefault();
			e.stopPropagation();
			setDragActive(false);

			// Check for generated image from AI Image Generator
			let generatedImageData = null;
			if (e.dataTransfer.types.includes('application/json')) {
				try {
					generatedImageData = JSON.parse(e.dataTransfer.getData('application/json'));
				} catch (error) {
					console.error('Error parsing JSON drag data: ', error);
				}
			}

			// Handle generated image drop
			if (
				generatedImageData &&
				generatedImageData.type === 'generated-image' &&
				generatedImageData.source === 'ai-image-generator'
			) {
				// Retrieve the file from the temporary global storage
				if (window.draggedFiles && window.draggedFiles['generated-image']) {
					const file = window.draggedFiles['generated-image'];

					// Respect mutual exclusion
					if (tempVideo) {
						message.warning('Remove video to add images');
						delete window.draggedFiles['generated-image'];
						return;
					}

					setTempImages((prev) => [...prev, file]);
					pushImagesToAll([file]);
					message.success('Generated image added to all posts');

					// Clean up
					delete window.draggedFiles['generated-image'];
					return;
				} else {
					message.error('Could not retrieve the generated image');
				}
			}

			const dtFiles = Array.from(e.dataTransfer?.files || []);
			if (!dtFiles.length) return;

			const hasVideoInDrop = dtFiles.some((f) => f.type.startsWith('video/'));
			const imageFilesInDrop = dtFiles.filter((f) => f.type.startsWith('image/'));

			// Respect mutual exclusion against current state
			if (tempVideo) {
				// Images are blocked while video is present → ignore dropped images
				return;
			}
			if (tempImages.length > 0) {
				// Video is blocked while images are present → ignore dropped videos
				if (hasVideoInDrop) return;
				if (imageFilesInDrop.length) {
					setTempImages((prev) => [...prev, ...imageFilesInDrop]);
					// PUSH to all employees
					pushImagesToAll(imageFilesInDrop);
				}
				return;
			}
			if (tempImages.length > 0) {
				// Video is blocked while images are present → ignore dropped videos
				if (hasVideoInDrop) {
					const firstVideo = dtFiles.find((f) => f.type.startsWith('video/'));

					if (firstVideo) {
						setTempVideo(firstVideo);
						// PUSH to all employees
						pushVideoToAll(firstVideo);
					}
					return;
				}
				if (imageFilesInDrop.length) {
					setTempImages((prev) => [...prev, ...imageFilesInDrop]);
					// PUSH to all employees
					pushImagesToAll(imageFilesInDrop);
				}
				return;
			}

			// Neither video nor images currently attached:
			// If the drop has any video → set first video (Reels case)
			if (hasVideoInDrop) {
				const firstVideo = dtFiles.find((f) => f.type.startsWith('video/'));
				if (firstVideo) setTempVideo(firstVideo);
				return;
			}

			// Otherwise, add images if any
			if (imageFilesInDrop.length) {
				setTempImages((prev) => [...prev, ...imageFilesInDrop]);
			}
		},
		[tempImages.length, tempVideo, pushImagesToAll, pushVideoToAll],
	);

	if (!available) {
		return (
			<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }} disabled>
				<Typography.Text type="secondary">
					{t('postPage.cards.mediaCardIsNotAvailable')}
				</Typography.Text>
			</Card>
		);
	}

	return (
		<Card bordered className="shadow-sm" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
			{/* Hidden inputs */}
			<input
				ref={hiddenImageInput}
				type="file"
				accept="image/*"
				multiple
				hidden
				onChange={handleImageInputChange}
			/>
			<input
				ref={hiddenVideoInput}
				type="file"
				accept="video/*"
				hidden
				onChange={handleVideoInputChange}
			/>

			{/* Header */}
			<Space
				direction={isMobile ? 'vertical' : 'horizontal'}
				style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'space-between' }}
				size={isMobile ? 8 : 12}
			>
				<Typography.Title level={5} style={{ margin: 0 }}>
					{t('postDistribution.cards.centralizedMediaStorage')}
				</Typography.Title>

				<Space wrap size={8} style={{ width: isMobile ? '100%' : 'auto' }}>
					{tempImages.length > 0 && (
						<Button
							danger
							onClick={handleResetImages}
							block={isMobile}
							size={isMobile ? 'middle' : 'default'}
						>
							{t('postDistribution.cards.removeImages')}
						</Button>
					)}

					<Button
						icon={<PictureOutlined />}
						onClick={handleClickImages}
						disabled={!!tempVideo} // block images if a video is present
						block={isMobile}
						size={isMobile ? 'middle' : 'default'}
					>
						{t('postDistribution.cards.addImages')}
					</Button>

					{tempVideo ? (
						<Button
							danger
							icon={<VideoCameraOutlined />}
							onClick={handleRemoveVideo}
							block={isMobile}
							size={isMobile ? 'middle' : 'default'}
						>
							{t('postDistribution.cards.removeVideo')}
						</Button>
					) : (
						<Tooltip title={tempImages.length ? 'Remove images to attach a video.' : undefined}>
							<Button
								icon={<VideoCameraOutlined />}
								disabled={tempImages.length > 0}
								onClick={handleClickVideo}
								block={isMobile}
								size={isMobile ? 'middle' : 'default'}
							>
								{t('postDistribution.cards.addVideo')}
							</Button>
						</Tooltip>
					)}
				</Space>
			</Space>

			<Divider style={{ margin: isMobile ? '10px 0' : '12px 0' }} />

			{/* Dropzone / preview area */}
			<div
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				onClick={tempImages.length ? undefined : handleClickImages}
				style={{
					border: tempImages.length
						? '1px solid #f0f0f0'
						: `2px dashed ${dragActive ? '#722ed1' : '#d9d9d9'}`,
					borderRadius: 12,
					padding: isMobile ? 12 : 16,
					background: dragActive ? '#f9f0ff' : '#fafafa',
					cursor: 'pointer',
					minHeight: isMobile ? 80 : 96,
				}}
			>
				{tempImages.length ? (
					<Space direction="vertical" style={{ width: '100%' }} size={12}>
						{imagePreviews.map(({ file, url }, index) => (
							<Card
								size="small"
								key={`${file.name}-${index}`}
								bodyStyle={{ padding: isMobile ? 10 : 12 }}
								draggable
								onDragStart={(e) => {
									// Store the file index and source in a way that's accessible
									const dragData = {
										type: 'centralized-image',
										source: 'central-media-card',
										fileIndex: index,
										fileName: file.name,
										fileType: file.type,
										fileSize: file.size,
									};

									e.dataTransfer.setData('application/json', JSON.stringify(dragData));
									// Also set as text as a fallback
									e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
									e.dataTransfer.effectAllowed = 'copy';

									// Store the actual file in a temporary global variable
									// This is a workaround since we can't transfer File objects directly
									if (!window.draggedFiles) window.draggedFiles = {};
									window.draggedFiles[`centralized-${index}`] = file;
								}}
								onDragEnd={(e) => {
									// Clean up the temporary storage for this file
									if (window.draggedFiles && window.draggedFiles[`centralized-${index}`]) {
										delete window.draggedFiles[`centralized-${index}`];
									}
								}}
							>
								<img
									alt="preview"
									src={url}
									style={{
										maxHeight: isMobile ? 160 : 200,
										width: '100%',
										objectFit: 'contain',
										borderRadius: 8,
										userSelect: 'none',
									}}
								/>
								<Space
									align="center"
									style={{
										width: '100%',
										justifyContent: 'space-between',
										marginTop: 8,
										gap: 8,
										flexWrap: 'wrap',
									}}
								>
									<Typography.Text
										type={(file.size / 1024).toFixed() > 10000 ? 'danger' : 'secondary'}
									>
										{(file.size / 1024).toFixed(2)} KB
									</Typography.Text>
									<Button
										danger
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleDeleteImage(index);
										}}
									>
										{t('postDistribution.cards.removeSingleImage')}
									</Button>
								</Space>
							</Card>
						))}
					</Space>
				) : (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={
							dragActive
								? t('postPage.dropzone.dropFilesToUpload')
								: t('postPage.dropzone.dragDropOrClick')
						}
					/>
				)}

				{tempVideo && (
					<div style={{ marginTop: 16 }}>
						<video
							className="mx-auto"
							ref={videoPlayerRef}
							style={{ width: '100%', height: 'auto', maxWidth: isMobile ? '100%' : 520 }}
							controls
						>
							{videoPreview && <source src={videoPreview} type="video/mp4" />}
						</video>
						{tempVideo && (
							<Typography.Paragraph
								style={{ textAlign: 'center', marginTop: 8 }}
								type={(tempVideo.size / 1_000_000).toFixed() > 200 ? 'danger' : 'secondary'}
							>
								{(tempVideo.size / 1_000_000).toFixed(2)} MB
							</Typography.Paragraph>
						)}
					</div>
				)}
			</div>
		</Card>
	);
};

export default MediaCardSelfContained;
